import React, { useRef, useEffect, useCallback, useState } from 'react';
// @ts-ignore
import { fabric } from 'fabric';
import { FloorLayoutImage, FloorSpace, SpaceObjectData } from '../types/floorLayout';
import { ParkingType } from '../types/parking';

interface FloorLayoutViewerProps {
  layoutImage?: FloorLayoutImage;
  spaces: FloorSpace[];
  spaceDefaults?: { width: number; height: number };
  parkingTypes?: ParkingType[];
  onSpaceClick?: (spaceId: string) => void;
}

const FloorLayoutViewer: React.FC<FloorLayoutViewerProps> = ({
  layoutImage,
  spaces,
  spaceDefaults = { width: 150, height: 75 },
  parkingTypes = [],
  onSpaceClick
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onSpaceClickRef = useRef(onSpaceClick);
  const [canvasReady, setCanvasReady] = useState(false);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onSpaceClickRef.current = onSpaceClick;
  }, [onSpaceClick]);

  // Helper function to get icon path based on status
  const getTypeIconPath = useCallback((typeName: string, status: string): string | null => {
    // If parkingTypes haven't loaded yet, return null
    if (!parkingTypes || parkingTypes.length === 0) {
      return null;
    }

    // Find the parking type by name
    const parkingType = parkingTypes.find(pt => pt.name === typeName);

    if (parkingType) {
      // Use selectedImage for CUSTOMER SUBMITTED or SOLD status, standardImage for all others
      const imagePath = (status === 'CUSTOMER SUBMITTED' || status === 'SOLD')
        ? parkingType.selectedImage
        : parkingType.standardImage;
      return imagePath || null;
    }

    return null;
  }, [parkingTypes]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    // Use fixed dimensions to ensure consistency between editor and viewer
    const canvasWidth = 1200;
    const canvasHeight = 800;

    try {
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: canvasWidth,
        height: canvasHeight,
        backgroundColor: '#f5f5f5',
        selection: false, // Disable selection in viewer mode
      });

      fabricCanvasRef.current = canvas;
      setCanvasReady(true);
    } catch (error) {
      console.error('FloorLayoutViewer: Error initializing canvas:', error);
    }

    // Cleanup
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setCanvasReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load background image
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvasReady) return;

    if (layoutImage?.url) {
      fabric.Image.fromURL(layoutImage.url, (img: any) => {
        if (!img || !canvas.getContext()) return;

        const scale = Math.min(
          (canvas.width || 1) / (img.width || 1),
          (canvas.height || 1) / (img.height || 1)
        ) * 0.9;

        img.scale(scale);
        img.set({
          left: ((canvas.width || 0) - (img.width || 0) * scale) / 2,
          top: ((canvas.height || 0) - (img.height || 0) * scale) / 2,
          selectable: false,
          evented: false,
        });

        canvas.setBackgroundImage(img, () => {
          canvas.renderAll();
        });
      });
    } else {
      // Clear background if no image
      canvas.setBackgroundImage(null as any, () => {
        canvas.renderAll();
      });
    }
  }, [layoutImage, canvasReady]);

  // Render spaces
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvasReady) return;

    // Clear existing space objects (keep background)
    const objectsToRemove = canvas.getObjects().filter((obj: any) => obj.spaceId);
    objectsToRemove.forEach(obj => canvas.remove(obj));

    // Add space objects
    spaces.forEach(space => {
      const position = space.spaceObject as SpaceObjectData;
      if (!position) return;

      const width = position.width || spaceDefaults.width;
      const height = position.height || spaceDefaults.height;

      // Create addon color dots if addons exist
      // Combine both regular addons and mandatory addons
      const allAddons = [...(space.addons || []), ...(space.mandatoryAddons || [])];
      const addonDots: fabric.Circle[] = [];
      if (allAddons.length > 0) {
        const dotRadius = 4;
        const dotSpacing = 10;
        const totalAddons = allAddons.length;

        allAddons.forEach((addon, index) => {
          if (addon.color) {
            const dot = new fabric.Circle({
              radius: dotRadius,
              fill: addon.color,
              stroke: '#ffffff',
              strokeWidth: 1.5,
              // Position dots at top-middle, stacked horizontally
              left: -((totalAddons - 1) * dotSpacing / 2) + (index * dotSpacing),
              top: -(height / 2) - dotRadius - 1,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            addonDots.push(dot);
          }
        });
      }

      // Helper function to create group with shape
      const createGroupWithShape = (shapeElement: fabric.Object) => {
        (shapeElement as any).spaceId = space._id;

        // Group shape and addon dots, then position the group (no text label in viewer mode)
        const groupItems: any[] = [...addonDots, shapeElement];
        const group = new fabric.Group(groupItems, {
          left: position.left || 0,
          top: position.top || 0,
          angle: position.angle || 0,
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: !!onSpaceClickRef.current,
          hoverCursor: 'pointer',
        });

        (group as any).spaceId = space._id;
        (group as any).spaceNumber = space.number;

        // Handle click using ref to avoid re-renders
        group.on('mousedown', () => {
          if (onSpaceClickRef.current) {
            onSpaceClickRef.current(space._id);
          }
        });

        canvas.add(group);
        canvas.renderAll();
      };

      // Get icon path for this space - always use status-based image
      const iconPath = getTypeIconPath(space.type, space.status);

      // Always load image from parking types
      if (iconPath) {
        fabric.Image.fromURL(iconPath, (img: fabric.Image) => {
          if (img && img.width && img.height) {
            const scale = Math.min(width / img.width, height / img.height);
            img.set({
              scaleX: scale,
              scaleY: scale,
              left: 0,
              top: 0,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            createGroupWithShape(img);
          } else {
            console.error(`Failed to load image for space ${space.number}:`, iconPath);
          }
        }, { crossOrigin: 'anonymous' });
      } else {
        console.error(`No image path found for space ${space.number}, type: ${space.type}, status: ${space.status}`);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, spaceDefaults, canvasReady, getTypeIconPath, parkingTypes]);

  return (
    <div ref={containerRef} style={{ width: '100%', minHeight: '600px', position: 'relative', overflow: 'auto' }}>
      {!canvasReady ? (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '14px',
          zIndex: 10
        }}>
          Loading canvas...
        </div>
      ) : !layoutImage && spaces.length === 0 ? (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '14px',
          zIndex: 10
        }}>
          <p>No layout image uploaded</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>Upload a layout image to get started</p>
        </div>
      ) : null}
      <canvas ref={canvasRef} />
    </div>
  );
};

export default FloorLayoutViewer;
